import {cn} from "#src/utils/cn";
import React, {forwardRef, useMemo} from 'react';

import type {IconProps} from './icon.types';

/**
 * IconReader: Renders icons with support for hex colors, Tailwind classes, and Ant Design tokens.
 */
export const IconReader = forwardRef<HTMLElement, IconProps>(({
																					 name,
																					 className = '',
																					 size,
																					 color,
																					 rotate,
																					 spin = false,
																					 flip,
																					 block = false,
																					 style,
																					 onClick,
																												 ...restProps
																											 }, ref) => {
	const isRawColor = useMemo(() => {
		if (!color)
			return false;
		return color.startsWith('#') || color.startsWith('rgb') || color.startsWith('hsl');
	}, [color]);

	const computedStyle = useMemo(() => {
		const styles: React.CSSProperties = {...style};
		if (size) {
			styles.fontSize = typeof size === 'number' ? `${size}px` : size;
		}
		if (color && (isRawColor || color.startsWith('var'))) {
			styles.color = color;
		}
		if (rotate) {
			styles.transform = `rotate(${rotate}deg)`;
		}
		if (flip) {
			const flipTransform = flip === 'horizontal'
				? 'scaleX(-1)'
				: flip === 'vertical'
					? 'scaleY(-1)'
					: 'scale(-1, -1)';
			styles.transform = styles.transform
				? `${styles.transform} ${flipTransform}`
				: flipTransform;
		}
		if (block) {
			styles.display = 'block';
		}
		return styles;
	}, [size, color, isRawColor, rotate, flip, block, style]);

	const iconClasses = [
		'icon',
		`icon-${name}`,
		spin ? 'icon-reader-spin' : '',
		!isRawColor && color ? color : '',
		className,
	].filter(Boolean).join(' ');

	return (
		<i
			ref={ref}
			className={cn(`${onClick ? 'hover:text-gray-400 dark:hover:text-gray-700 cursor-pointer transition-colors' : ''}`, iconClasses)}
			style={computedStyle}
			onClick={onClick}
			aria-hidden="true"
			{...restProps}
		/>
	);
});

// Set a displayName for easier debugging
IconReader.displayName = 'IconReader';
